/**
 * Formatting and label mapping utilities across Candidate and Org portals.
 */

/**
 * Maps raw backend role enums to clean, consistent human-readable labels.
 * Used across Settings -> Profile, Organizations, and Org Settings.
 */
export function formatRole(role) {
  if (!role) return '';
  const upper = String(role).toUpperCase().trim();
  switch (upper) {
    case 'OWNER':
    case 'ORG_OWNER':
      return 'Owner';
    case 'ORG_ADMIN':
    case 'ADMIN':
      return 'Admin';
    case 'SUPER_ADMIN':
      return 'Platform Admin';
    case 'INTERVIEWER':
      return 'Interviewer';
    case 'MEMBER':
    case 'ORG_MEMBER':
    case 'CONTACT':
      return 'Member';
    case 'CANDIDATE':
      return 'Candidate';
    case 'RECRUITER':
      return 'Recruiter';
    default:
      return upper
        .toLowerCase()
        .split('_')
        .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
        .join(' ');
  }
}

/**
 * Maps interview modes and enums to clean human-readable labels.
 */
export function formatInterviewType(type) {
  if (!type) return 'Interview';
  const upper = String(type).toUpperCase().trim();
  switch (upper) {
    case 'VOICE_ADAPTIVE':
    case 'ADAPTIVE_VOICE':
      return 'Adaptive Voice Interview';
    case 'CODING_TECHNICAL':
    case 'CODING':
      return 'Coding & Technical Interview';
    case 'SYSTEM_DESIGN':
      return 'System Design Interview';
    case 'BEHAVIORAL':
      return 'Behavioral Interview';
    case 'MOCK_VOICE':
      return 'Voice Mock Session';
    case 'MOCK_CODING':
      return 'Coding Mock Session';
    case 'SUPERVISED':
      return 'Supervised Session';
    case 'AUTONOMOUS':
    case 'ARIA':
      return 'Autonomous (Aria)';
    default:
      return upper
        .toLowerCase()
        .split('_')
        .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
        .join(' ');
  }
}

/**
 * Maps application pipeline statuses to status label and chip class.
 */
export function getApplicationStatusChip(status) {
  const norm = String(status || '').toUpperCase().trim();
  switch (norm) {
    case 'OFFER':
    case 'ACCEPTED':
    case 'HIRED':
      return { label: 'Offer', chipClass: 'chip-teal-solid' };
    case 'INTERVIEW_SCHEDULED':
    case 'INTERVIEW':
    case 'SCHEDULED':
      return { label: 'Interview scheduled', chipClass: 'chip-teal-solid' };
    case 'IN_REVIEW':
    case 'REVIEW':
    case 'SCREENING':
    case 'SHORTLISTED':
      return { label: 'Under review', chipClass: 'chip-teal' };
    case 'APPLIED':
    case 'SUBMITTED':
    case 'NEW':
      return { label: 'Applied', chipClass: 'chip-navy' };
    case 'NOT_SELECTED':
    case 'REJECTED':
    case 'ARCHIVED':
    case 'WITHDRAWN':
      return { label: 'Not selected', chipClass: 'chip-rose' };
    default:
      return {
        label: norm.toLowerCase().replace(/_/g, ' '),
        chipClass: 'chip-navy',
      };
  }
}

/**
 * Maps interview verdict / fit rating to clean chip.
 */
export function getFitVerdictChip(verdict) {
  const v = String(verdict || '').toLowerCase().trim();
  // Backend P0-09 closeout: an integrity-policy ending is a process disposition
  // (INTEGRITY_REVIEW), never a fit / performance verdict.
  if (v === 'integrity_review') {
    return { label: 'Under review', chipClass: 'chip-navy' };
  }
  if (v.includes('positive') || v.includes('strong') || v.includes('fit') && !v.includes('not')) {
    return { label: 'Positive fit', chipClass: 'chip-teal-solid' };
  }
  if (v.includes('not') || v.includes('reject') || v.includes('fail')) {
    return { label: 'Not a fit', chipClass: 'chip-rose' };
  }
  return { label: 'Needs work', chipClass: 'chip-teal' };
}
